import type { Row } from "./api";

export interface AddressComponents {
  street: string;
  unit: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export interface AddressBinding {
  kind: "IRS_MAILING_ADDRESS_V1";
  fields: {
    street: "street";
    unit: "apartment";
    city: "city";
    state: "state";
    postalCode: "zip";
    country: "foreign_country";
    foreignProvince: "foreign_province";
    foreignPostal: "foreign_postal";
  };
  countryEncoding: "NULL_MEANS_US";
  nullableFields: readonly string[];
}

const isObject = (value: unknown): value is Row =>
  value !== null && typeof value === "object" && !Array.isArray(value);
const owns = (value: object, key: string) =>
  Object.prototype.hasOwnProperty.call(value, key);
const stringValue = (value: unknown) =>
  typeof value === "string" ? value : "";
const componentKeys: readonly (keyof AddressComponents)[] = [
  "street",
  "unit",
  "city",
  "state",
  "postalCode",
  "country",
];
const exactFields: AddressBinding["fields"] = {
  street: "street",
  unit: "apartment",
  city: "city",
  state: "state",
  postalCode: "zip",
  country: "foreign_country",
  foreignProvince: "foreign_province",
  foreignPostal: "foreign_postal",
};
const nullableFields = [
  "apartment",
  "foreign_country",
  "foreign_province",
  "foreign_postal",
] as const;

function stringLeaf(schema: unknown, nullable: boolean): boolean {
  if (!isObject(schema) || schema.$ref || schema.allOf || schema.if)
    return false;
  if (!nullable)
    return schema.type === "string" && !schema.anyOf && !schema.oneOf;
  const branches = schema.anyOf ?? schema.oneOf;
  return (
    Array.isArray(branches) &&
    branches.length === 2 &&
    branches.some((branch) => isObject(branch) && branch.type === "null") &&
    branches.some((branch) => stringLeaf(branch, false))
  );
}

/** Detect only the concrete current supplemental-address shape, never a name-based guess. */
export function addressBinding(schema: Row): AddressBinding | null {
  if (
    !isObject(schema) ||
    schema.type !== "object" ||
    !isObject(schema.properties) ||
    schema.$ref ||
    schema.anyOf ||
    schema.oneOf ||
    schema.allOf ||
    schema.if
  )
    return null;
  for (const field of Object.values(exactFields)) {
    if (
      !stringLeaf(
        schema.properties[field],
        nullableFields.includes(field as (typeof nullableFields)[number]),
      )
    )
      return null;
  }
  return {
    kind: "IRS_MAILING_ADDRESS_V1",
    fields: { ...exactFields },
    countryEncoding: "NULL_MEANS_US",
    nullableFields: [...nullableFields],
  };
}

export const detectAddressSchema = addressBinding;

/** These are explicit country spellings, not an inference from a ZIP code, city or state. */
export function isUSAddressCountry(country: string): boolean {
  return ["US", "USA", "UNITED STATES", "UNITED STATES OF AMERICA"].includes(
    country.trim().toUpperCase(),
  );
}

/** Read-only view. A missing country is unknown; only an explicitly stored null encodes US. */
export function readStructuredAddress(
  value: unknown,
  binding: AddressBinding,
): AddressComponents {
  const source = isObject(value) ? value : {};
  const fields = binding.fields;
  const country =
    source[fields.country] === null
      ? "US"
      : stringValue(source[fields.country]);
  const foreign = country !== "" && !isUSAddressCountry(country);
  return {
    street: stringValue(source[fields.street]),
    unit: stringValue(source[fields.unit]),
    city: stringValue(source[fields.city]),
    state: stringValue(source[foreign ? fields.foreignProvince : fields.state]),
    postalCode: stringValue(
      source[foreign ? fields.foreignPostal : fields.postalCode],
    ),
    country,
  };
}

/**
 * Explicit local draft edit only. The caller still performs source-schema validation and Save.
 * Unknown original properties survive so this adapter cannot hide a legacy validation problem.
 * No formatted-address parser, postal lookup, evidence update, validity claim or network request.
 */
export function writeStructuredAddress(
  value: unknown,
  next: AddressComponents,
  binding: AddressBinding,
): Row {
  if (
    !isObject(next) ||
    componentKeys.some((key) => typeof next[key] !== "string")
  ) {
    throw new TypeError("Address components must be separate text values.");
  }
  if (value !== undefined && value !== null && !isObject(value)) {
    throw new TypeError(
      "The saved address is not a structured object. It must be repaired before replacing its components.",
    );
  }
  const source = isObject(value) ? value : {};
  const result = { ...source };
  const before = readStructuredAddress(source, binding);
  if (componentKeys.every((key) => next[key] === before[key])) return result;
  const fields = binding.fields;
  const knownUS = isUSAddressCountry(next.country);
  const knownForeign = next.country.trim() !== "" && !knownUS;

  const put = (
    field: string,
    component: keyof AddressComponents,
    nullable = false,
  ) => {
    // Do not materialize untouched missing controls as false knowledge or empty defaults.
    if (
      owns(source, field) ||
      next[component] !== "" ||
      next[component] !== before[component]
    ) {
      result[field] =
        nullable && next[component] === "" ? null : next[component];
    }
  };
  put(fields.street, "street");
  put(fields.unit, "unit", true);
  put(fields.city, "city");

  if (knownUS) {
    put(fields.state, "state");
    put(fields.postalCode, "postalCode");
    // Explicitly selected US components use the existing IRS-shaped persistence encoding.
    // The only optional unit representation in this source contract is a string or null.
    if (!owns(result, fields.unit) && next.unit === "")
      result[fields.unit] = null;
    result[fields.country] = null;
    result[fields.foreignProvince] = null;
    result[fields.foreignPostal] = null;
  } else if (knownForeign) {
    result[fields.country] = next.country;
    put(fields.foreignProvince, "state", true);
    put(fields.foreignPostal, "postalCode", true);
    // Domestic state/zip are intentionally retained, not filled with invented placeholders.
    // The current source requires those fields even for a foreign address; validation remains authoritative.
  } else {
    // Clearing a country is unresolved, never implicit US. Preserve hidden foreign components.
    if (owns(source, fields.country) || next.country !== before.country)
      result[fields.country] = "";
    const beforeForeign =
      before.country !== "" && !isUSAddressCountry(before.country);
    put(
      beforeForeign ? fields.foreignProvince : fields.state,
      "state",
      beforeForeign,
    );
    put(
      beforeForeign ? fields.foreignPostal : fields.postalCode,
      "postalCode",
      beforeForeign,
    );
  }
  return result;
}

/** Actual source locations, not a claim that catalog-only or artifact-dependent capture is enabled. */
export const addressLocationInventory = [
  {
    id: "INT.taxpayer.mailing_address",
    source:
      "outputs/ZentroTax_Phase_2B_4_Detailed_Implementation_Package/facts/supplemental-values.schema.json",
    pointer: "/oneOf/2/properties/value",
    status: "LIVE_SUPPLEMENTAL_DRAFT",
    binding: "IRS_MAILING_ADDRESS_V1",
  },
  {
    id: "INT.return.preparer_profile.address",
    source:
      "outputs/ZentroTax_Phase_2B_4_Detailed_Implementation_Package/facts/supplemental-values.schema.json",
    pointer: "/oneOf/9/properties/value/properties/address/anyOf/0",
    status: "ARTIFACT_DEPENDENT_PARENT_NOT_LIVE",
    binding: "IRS_MAILING_ADDRESS_V1",
  },
  {
    id: "person_identity.mailing_address_artifact_id",
    source:
      "outputs/ZentroTax_Phase_2B_4_Detailed_Implementation_Package/catalog/fact-catalog.json",
    pointer:
      "/type_registry/person_identity/properties/mailing_address_artifact_id",
    status: "ARTIFACT_REFERENCE_NOT_CAPTURE",
    binding: null,
  },
  {
    id: "W2.c",
    source:
      "outputs/ZentroTax_Phase_2B_4_Detailed_Implementation_Package/w2/w2-record.schema.json",
    pointer: "/properties/fields/properties/box_c_employer_name_address_zip",
    status: "SOURCE_DOCUMENT_CAPTURE_NOT_LIVE",
    binding: null,
  },
  {
    id: "W2.f",
    source:
      "outputs/ZentroTax_Phase_2B_4_Detailed_Implementation_Package/w2/w2-record.schema.json",
    pointer: "/properties/fields/properties/box_f_employee_address_zip",
    status: "SOURCE_DOCUMENT_CAPTURE_NOT_LIVE",
    binding: null,
  },
  {
    id: "F1040.address",
    source:
      "outputs/ZentroTax_Phase_2B_7_Detailed_Implementation_Package/forms/form1040-bindings.json",
    pointer: "/fields",
    status: "OUTPUT_FIELD_MAPPING_NOT_CAPTURE",
    binding: null,
  },
  {
    id: "F1040.preparer.address",
    source:
      "outputs/ZentroTax_Phase_2B_7_Detailed_Implementation_Package/forms/form1040-bindings.json",
    pointer: "/fields/195",
    status: "OUTPUT_FIELD_MAPPING_NOT_CAPTURE",
    binding: null,
  },
] as const;
